import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Crown, Sparkles, ShieldOff, Zap, Check, Landmark, Lock, ArrowDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/seo";
import { startCheckout } from "@/lib/checkout";
import api, { apiError, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";

const PERKS = [
  { icon: Sparkles, title: "Exclusive analysis", desc: "Premium deep dives and market intelligence you won't find in the free feed." },
  { icon: Landmark, title: "Regulation Radar", desc: "The weekly policy briefing email every Monday — plus full access to the issue archive." },
  { icon: ShieldOff, title: "Completely ad-free", desc: "Every banner, sidebar and in-feed ad disappears the moment you upgrade." },
  { icon: Zap, title: "Support the newsroom", desc: "Keep NewsLayer independent and reader-funded." },
];

const PLANS = [
  { key: "premium_monthly", name: "Monthly", price: "$9.99", per: "/month", note: "Cancel anytime" },
  { key: "premium_annual", name: "Annual", price: "$99", per: "/year", note: "2 months free", featured: true },
];

const ReadTeaser = ({ slug }: { slug: string }) => {
  const { data } = useQuery({
    queryKey: ["premium-teaser", slug],
    queryFn: async () => (await api.get(`/articles/${slug}`)).data,
    retry: 1,
  });
  const a = data?.article;
  const para = (a?.body_teaser || "").match(/<p[^>]*>[\s\S]*?<\/p>/)?.[0];
  if (!a || !para) return null;
  return (
    <div data-testid="premium-read-teaser"
      className="mt-10 flex rounded-xl border border-[#7C3AED]/30 bg-[#0d0a16] overflow-hidden shadow-[0_0_50px_-22px_rgba(124,58,237,0.5)]">
      <div className="flex-1 min-w-0 p-6 sm:p-7">
        <span className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 font-heading font-bold text-[9px] tracking-[0.16em] uppercase text-white bg-gradient-to-r from-[#7C3AED] to-[#8B5CF6]">
          <Lock className="h-3 w-3" aria-hidden /> Premium preview
        </span>
        <h2 data-testid="premium-teaser-title" className="font-news font-bold text-white text-xl sm:text-2xl leading-snug mt-3.5">
          {a.title}
        </h2>
        <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-2">
          {timeAgo(a.published_at)}{a.read_minutes ? ` · ${a.read_minutes} min read` : ""}
        </p>
        <div data-testid="premium-teaser-body"
          className="text-[15px] text-gray-300 leading-relaxed mt-4 [mask-image:linear-gradient(to_bottom,black_30%,transparent_100%)]"
          dangerouslySetInnerHTML={{ __html: para }} />
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-widest text-[#F59E0B] flex items-center gap-1.5">
          <ArrowDown className="h-3.5 w-3.5 animate-bounce" aria-hidden /> The full analysis unlocks with Premium
        </p>
      </div>
      {a.hero_image_url && (
        <div className="hidden md:block relative w-52 shrink-0">
          <img src={a.hero_image_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-80" />
          <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#0d0a16] to-transparent" />
        </div>
      )}
    </div>
  );
};

export default function Premium() {
  useSEO("Go Premium — NewsLayer", "Unlock exclusive crypto analysis and an ad-free NewsLayer experience. $9.99/month or $99/year.");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const readSlug = params.get("read");
  const [busy, setBusy] = useState("");

  const subscribe = async (key: string) => {
    if (!user) { navigate("/login"); return; }
    if (readSlug) localStorage.setItem("nl_read_after_upgrade", readSlug);
    setBusy(key);
    try { await startCheckout(key); }
    catch (e) { toast.error(apiError(e)); setBusy(""); }
  };

  return (
    <div data-testid="premium-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#F59E0B] mb-4">
        <Crown className="h-4 w-4" /> NewsLayer Premium
      </div>
      <h1 className="font-heading font-black text-white text-[clamp(1.375rem,6.5vw,3rem)] leading-tight max-w-3xl whitespace-nowrap">
        Sharper signal. Zero ads.
      </h1>
      <p className="text-gray-400 mt-5 max-w-2xl text-base sm:text-lg leading-relaxed">
        Premium unlocks exclusive analysis and strips every ad off the site — so it's just you and the news that moves markets.
      </p>

      {readSlug && !user?.is_premium && <ReadTeaser slug={readSlug} />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
        {PERKS.map((p) => (
          <div key={p.title} className="bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-[#F59E0B]/40 transition-colors">
            <p.icon className="h-7 w-7 text-[#F59E0B] mb-4" />
            <h3 className="font-heading font-bold text-white text-lg">{p.title}</h3>
            <p className="text-sm text-gray-400 mt-2 leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>

      {user?.is_premium ? (
        <div data-testid="premium-active-state" className="mt-12 bg-[#1a1505] border border-[#F59E0B]/40 rounded-xl p-8 text-center">
          <Crown className="h-8 w-8 text-[#F59E0B] mx-auto mb-3" />
          <h2 className="font-heading font-bold text-white text-2xl">You're Premium ✓</h2>
          <p className="text-gray-400 mt-2">Enjoy the ad-free experience and exclusive analysis. Thank you for supporting NewsLayer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-12 max-w-2xl">
          {PLANS.map((p) => (
            <div key={p.key} data-testid={`premium-plan-${p.key}`}
              className={`rounded-xl p-7 border ${p.featured ? "bg-[#1a1505] border-[#F59E0B]/50" : "bg-[#121212] border-white/10"}`}>
              {p.featured && (
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#F59E0B] border-l-2 border-[#F59E0B] pl-2 py-0.5">
                  Best value
                </span>
              )}
              <h3 className="font-heading font-bold text-white text-lg mt-3">{p.name}</h3>
              <p className="mt-2"><span className="font-mono text-4xl font-bold text-white">{p.price}</span>
                <span className="text-gray-500 text-sm">{p.per}</span></p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1.5 flex items-center gap-1">
                <Check className="h-3 w-3 text-green-400" /> {p.note}
              </p>
              <Button onClick={() => subscribe(p.key)} disabled={!!busy}
                data-testid={`premium-subscribe-${p.key}`}
                className={`sparkle-btn w-full mt-6 font-bold ${p.featured ? "bg-[#F59E0B] text-black hover:bg-[#d98b09]" : "bg-white/10 text-white hover:bg-white/20"}`}>
                {busy === p.key ? "Redirecting…" : `Go Premium ${p.name.toLowerCase()}`}
              </Button>
            </div>
          ))}
        </div>
      )}
      <p className="font-mono text-[10px] text-gray-600 mt-6">Payments processed securely by Stripe. Cancel anytime from your card provider.</p>
    </div>
  );
}
